/**
 * BlackboardValueCondition - behavior tree condition checking blackboard values.
 */

import { EngineClass } from '../../../../systems/ClassRegistry.js';
import { BehaviorStatus } from '../../BehaviorNode.js';
import { ConditionEvaluator, type ConditionEvaluatorOptions } from '../ConditionEvaluator.js';

import type { Blackboard } from '../../blackboard/Blackboard.js';

export type ComparisonOperator =
  | 'equals'
  | 'notEquals'
  | 'greaterThan'
  | 'greaterThanOrEqual'
  | 'lessThan'
  | 'lessThanOrEqual'
  | 'exists'
  | 'notExists';

export interface BlackboardValueConditionOptions extends ConditionEvaluatorOptions {
  key: string;
  value?: any;
  operator?: ComparisonOperator;
  valueType?: 'string' | 'number' | 'boolean' | 'object';
}

@EngineClass('BlackboardValueCondition')
export class BlackboardValueCondition extends ConditionEvaluator {
  private key: string;
  private value: any;
  private operator: ComparisonOperator;
  private valueType?: string;

  constructor(options: BlackboardValueConditionOptions) {
    super({
      name: `BlackboardValue[${options.key}]`,
      ...options
    });

    this.key = options.key;
    this.value = options.value;
    this.operator = options.operator ?? 'equals';
    this.valueType = options.valueType;

    // Validate that value is provided for operators that need it
    if (this.operator !== 'exists' && this.operator !== 'notExists' && options.value === undefined) {
      throw new Error(`BlackboardValueCondition: value is required for operator '${this.operator}'`);
    }
  }

  protected override async onEvaluate(blackboard: Blackboard): Promise<BehaviorStatus> {
    const actualValue = blackboard.getValue(this.key);

    switch (this.operator) {
      case 'exists':
        return actualValue !== undefined ? BehaviorStatus.Success : BehaviorStatus.Failure;

      case 'notExists':
        return actualValue === undefined ? BehaviorStatus.Success : BehaviorStatus.Failure;

      case 'equals':
        return this.compareValues(actualValue, this.value, '===') ? BehaviorStatus.Success : BehaviorStatus.Failure;

      case 'notEquals':
        return this.compareValues(actualValue, this.value, '!==') ? BehaviorStatus.Success : BehaviorStatus.Failure;

      case 'greaterThan':
        return this.compareValues(actualValue, this.value, '>') ? BehaviorStatus.Success : BehaviorStatus.Failure;

      case 'greaterThanOrEqual':
        return this.compareValues(actualValue, this.value, '>=') ? BehaviorStatus.Success : BehaviorStatus.Failure;

      case 'lessThan':
        return this.compareValues(actualValue, this.value, '<') ? BehaviorStatus.Success : BehaviorStatus.Failure;

      case 'lessThanOrEqual':
        return this.compareValues(actualValue, this.value, '<=') ? BehaviorStatus.Success : BehaviorStatus.Failure;

      default:
        console.warn(`BlackboardValueCondition: Unknown operator '${this.operator}'`);
        return BehaviorStatus.Failure;
    }
  }

  private compareValues(actualValue: any, expectedValue: any, operator: string): boolean {
    // Handle undefined/null values
    if (actualValue === undefined || actualValue === null) {
      if (operator === '===' || operator === '!==') {
        return operator === '===' ? actualValue === expectedValue : actualValue !== expectedValue;
      }
      return false; // Can't do numeric comparisons with undefined/null
    }

    // Handle numeric comparisons
    if (operator === '>' || operator === '>=' || operator === '<' || operator === '<=') {
      const actualNum = Number(actualValue);
      const expectedNum = Number(expectedValue);

      if (isNaN(actualNum) || isNaN(expectedNum)) {
        console.warn(`BlackboardValueCondition: Cannot perform numeric comparison on non-numeric values: ${actualValue} ${operator} ${expectedValue}`);
        return false;
      }

      switch (operator) {
        case '>': return actualNum > expectedNum;
        case '>=': return actualNum >= expectedNum;
        case '<': return actualNum < expectedNum;
        case '<=': return actualNum <= expectedNum;
        default: return false;
      }
    }

    // Handle equality comparisons
    if (operator === '===') {
      return actualValue === expectedValue;
    }

    if (operator === '!==') {
      return actualValue !== expectedValue;
    }

    return false;
  }

  public override getDebugInfo(): { name: string; description: string; key: string; value: any; operator: string } {
    return {
      ...super.getDebugInfo(),
      key: this.key,
      value: this.value,
      operator: this.operator
    };
  }
}